import { useSEO } from '@/hooks/useSEO';
import { useState } from "react";
import PageNavbar from "@/components/feature/PageNavbar";
import Footer from "@/pages/home/components/Footer";
import { submitForm } from "@/lib/submitForm";

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://isman.co.ke';

const PLUMBING_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Commercial & Industrial Plumbing Services Kenya",
    "url": SITE_URL + "/plumbing-services",
    "description": "Licensed commercial and industrial plumbing services in Kenya — hot & cold water systems, gas pipework, medical gas, drainage and preventive maintenance by ISMAN Engineering Nairobi.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "ISMAN Engineering Company",
      "url": SITE_URL,
      "telephone": "+254724114555"
    },
    "areaServed": { "@type": "Country", "name": "Kenya" },
    "serviceType": "Commercial Plumbing"
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
      { "@type": "ListItem", "position": 2, "name": "Plumbing Services", "item": SITE_URL + "/plumbing-services" }
    ]
  }
];

const offerings = [
  { icon: "ri-drop-line", title: "Cold & Hot Water Systems", desc: "Design and installation of cold and hot water supply networks for hotels, hospitals, schools and commercial buildings." },
  { icon: "ri-water-flash-line", title: "Drainage & Sewage", desc: "Above and below ground drainage systems — floor drains, grease traps, and connection to municipal sewer." },
  { icon: "ri-fire-fill", title: "Gas Pipework", desc: "LPG and natural gas pipework installation, pressure testing and commissioning for commercial kitchens and industrial boilers." },
  { icon: "ri-hospital-line", title: "Medical Gas Systems", desc: "Piped oxygen, medical air and vacuum systems for hospitals and clinics — compliant with KEBS and WHO guidelines." },
  { icon: "ri-shield-check-line", title: "Backflow Prevention", desc: "Pressure-reducing valves, check valves, and backflow prevention devices protecting water quality." },
  { icon: "ri-settings-3-line", title: "Maintenance Contracts", desc: "Scheduled preventive maintenance for all plumbing systems — leak detection, pipe insulation and valve servicing." },
];

const process = [
  { step: "01", title: "Site Assessment", desc: "We survey existing services, identify constraints, and confirm connection points for water, gas and drainage." },
  { step: "02", title: "System Design", desc: "Hydraulic calculations and detailed plumbing drawings prepared for client and contractor review." },
  { step: "03", title: "Installation", desc: "Our licensed plumbers install all pipework, fixtures and fittings to drawing specifications." },
  { step: "04", title: "Testing & Commissioning", desc: "Pressure tests, flow rate verification and leak checks before handover with full commissioning documentation." },
];

export default function PlumbingServicesPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", type: "Commercial Hot & Cold Water", details: "" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const data = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => data.append(k, v));
    try {
      await submitForm("plumbing-services", data);
    } catch (_e) { /* ignore */ }
    setSubmitting(false);
    setSubmitted(true);
  };

  useSEO({
    title: 'Commercial & Industrial Plumbing Services Kenya | ISMAN Engineering Nairobi',
    description: 'Licensed commercial plumbing services in Kenya — hot & cold water systems, industrial gas pipework, medical gas, drainage and maintenance. ISMAN Engineering Nairobi.',
    keywords: 'commercial plumbing Kenya, industrial plumbing Nairobi, gas pipework Kenya, medical gas Kenya, drainage systems Nairobi, plumbing contractor Kenya',
    canonical: SITE_URL + '/plumbing-services',
    schema: PLUMBING_SCHEMA,
  });

  return (
    <main className="min-h-screen bg-white">
      <PageNavbar />
      <section className="relative h-[480px] flex items-end overflow-hidden">
        <img src="/images/home-services/plumbing.webp" alt="Plumbing Services" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6 pb-14 w-full">
          <span className="inline-block bg-teal-600 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-md mb-4">ISMAN Engineering</span>
          <h1 className="text-white text-4xl md:text-5xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Commercial &amp; Industrial Plumbing</h1>
          <p className="text-white/75 text-base max-w-2xl">Complete plumbing solutions — from hot and cold water systems to medical gas pipework — designed, installed and maintained by ISMAN&#39;s licensed team.</p>
        </div>
      </section>

      <section className="py-16 bg-gray-50 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-600 text-xs font-bold uppercase tracking-widest">What We Do</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>Plumbing Services</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {offerings.map((o) => (
              <div key={o.title} className="bg-white rounded-xl p-6 border border-gray-100 hover:border-teal-200 transition-all group">
                <div className="w-11 h-11 flex items-center justify-center bg-teal-50 rounded-lg mb-4 group-hover:bg-teal-600 transition-colors">
                  <i className={`${o.icon} text-teal-600 group-hover:text-white text-xl transition-colors`}></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{o.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{o.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 md:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Recent Projects</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { seq: "pg1", img: "/images/water/purification-b.webp", label: "Hot & Cold Water Systems" },
              { seq: "pg2", img: "/images/hospital/mortuary-lab.webp", label: "Hospital & Lab Plumbing" },
              { seq: "pg3", img: "/images/kitchen/sinks.webp", label: "Kitchen Sinks & Grease Traps" },
              { seq: "pg4", img: "/images/services/install-b.webp", label: "Stainless Process Pipework" },
              { seq: "pg5", img: "/images/water/dosing-a.webp", label: "Water Dosing & Distribution" },
              { seq: "pg6", img: "/images/pneumatic/installation.webp", label: "Valves, Manifolds & Fittings" },
            ].map((g) => (
              <div key={g.seq} className="relative rounded-lg overflow-hidden group h-48">
                <img src={g.img} loading="lazy" alt={g.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <span className="text-white text-sm font-semibold">{g.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-10 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Our Installation Process</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p) => (
              <div key={p.step} className="text-center">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-black text-lg">{p.step}</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{p.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="inquiry" className="py-16 px-4 md:px-6 bg-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Request a Plumbing Quote</h2>
          <p className="text-gray-500 text-sm text-center mb-8">Tell us about your plumbing needs and we&apos;ll respond within 1 business day.</p>
          {submitted ? (
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-8 text-center">
              <i className="ri-check-double-line text-4xl text-teal-600 mb-3 block"></i>
              <h3 className="font-bold text-gray-900 mb-1">Enquiry Received!</h3>
              <p className="text-gray-500 text-sm">Our plumbing team will be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-isman-form>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Full Name *</label>
                  <input required name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400" placeholder="Sarah Njeri" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Email *</label>
                  <input required type="email" name="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400" placeholder="sarah@example.com" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Phone *</label>
                  <input required name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400" placeholder="0722 000 000" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Service Type *</label>
                  <select required name="type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 bg-white cursor-pointer">
                    {["Commercial Hot & Cold Water", "Industrial Gas Pipework", "Medical Gas Systems", "Drainage & Sewage", "Maintenance Contract"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">Project Details</label>
                <textarea name="details" value={form.details} onChange={(e) => { if (e.target.value.length <= 500) setForm({ ...form, details: e.target.value }); }} rows={4} maxLength={500} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 resize-none" placeholder="Describe your plumbing project..."></textarea>
                <p className="text-xs text-gray-400 text-right mt-1">{form.details.length}/500</p>
              </div>
              <button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white font-bold py-3 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                {submitting ? <><i className="ri-loader-4-line animate-spin"></i> Sending...</> : <><i className="ri-send-plane-line"></i> Send Enquiry</>}
              </button>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
